const DESKTOP_BREAKPOINT = '(min-width: 992px)'
const MOBILE_SCROLL_THRESHOLD = 8
const DESKTOP_SCROLL_DELTA = 2

export function getIsNotTopState(currentScrollY) {
  return currentScrollY > MOBILE_SCROLL_THRESHOLD
}

export function getScrollClassState({
  isDesktop,
  currentScrollY,
  previousScrollY,
  isScrolled,
}) {
  if (!isDesktop) {
    return currentScrollY > MOBILE_SCROLL_THRESHOLD
  }

  if (currentScrollY <= 1) {
    return false
  }

  if (currentScrollY > previousScrollY + DESKTOP_SCROLL_DELTA) {
    return true
  }

  if (currentScrollY < previousScrollY - DESKTOP_SCROLL_DELTA) {
    return false
  }

  return isScrolled
}

export function initUpdatedNavbar() {
  const navbar = document.querySelector('.navbar__main')

  if (!navbar || navbar.dataset.navbarInitialized === 'true') {
    return
  }

  const overlay = navbar.querySelector('.navbar__overlay-dropdown')
  const menuButton = navbar.querySelector('.w-nav-button')
  const desktop = window.matchMedia(DESKTOP_BREAKPOINT)

  if (!overlay) {
    return
  }

  const items = Array.from(
    navbar.querySelectorAll('.navbar__primary--dropdown-upd')
  )
    .map(trigger => {
      const submenu = trigger.nextElementSibling
      const wrapper = trigger.closest('.navbar__primary--link')

      if (
        !wrapper ||
        !submenu?.classList.contains('navbar__primary--submenu-upd')
      ) {
        return null
      }

      return {trigger, wrapper, submenu}
    })
    .filter(Boolean)

  if (!items.length) {
    return
  }

  navbar.dataset.navbarInitialized = 'true'

  let activeItem = null
  let previousScrollY = Math.max(window.scrollY, 0)
  let isScrolled = false

  function updateScrollState() {
    const currentScrollY = Math.max(window.scrollY, 0)

    isScrolled = getScrollClassState({
      isDesktop: desktop.matches,
      currentScrollY,
      previousScrollY,
      isScrolled,
    })

    navbar.classList.toggle('is-scrolled', isScrolled)
    navbar.classList.toggle('is-not-top', getIsNotTopState(currentScrollY))
    previousScrollY = currentScrollY
  }

  function getOverlayHeight(submenu) {
    const navbarRect = navbar.getBoundingClientRect()
    const submenuRect = submenu.getBoundingClientRect()

    return Math.ceil(submenuRect.bottom - navbarRect.top)
  }

  function setItemState(item, isOpen) {
    item.wrapper.classList.toggle('is-open', isOpen)
    item.trigger.setAttribute('aria-expanded', String(isOpen))
  }

  function closeAllDropdowns() {
    items.forEach(item => {
      setItemState(item, false)
      item.submenu.style.height = desktop.matches ? '' : '0px'
    })

    activeItem = null
    overlay.classList.remove('is-open')
    overlay.style.height = '0px'
    navbar.classList.remove('is-dropdown-open')
  }

  function toggleDesktopDropdown(item) {
    const isCurrentItemOpen =
      activeItem === item && item.wrapper.classList.contains('is-open')

    if (isCurrentItemOpen) {
      closeAllDropdowns()
      return
    }

    items.forEach(currentItem => {
      setItemState(currentItem, currentItem === item)
      currentItem.submenu.style.height = ''
    })

    activeItem = item
    navbar.classList.add('is-dropdown-open')
    overlay.classList.add('is-open')
    overlay.style.height = `${getOverlayHeight(item.submenu)}px`
  }

  function toggleMobileDropdown(item) {
    const shouldOpen =
      activeItem !== item || !item.wrapper.classList.contains('is-open')

    items.forEach(currentItem => {
      setItemState(currentItem, false)
      currentItem.submenu.style.height = '0px'
    })

    if (!shouldOpen) {
      activeItem = null
      return
    }

    setItemState(item, true)
    activeItem = item

    window.requestAnimationFrame(() => {
      if (activeItem === item) {
        item.submenu.style.height = `${item.submenu.scrollHeight}px`
      }
    })
  }

  function toggleDropdown(item) {
    if (desktop.matches) {
      toggleDesktopDropdown(item)
    } else {
      toggleMobileDropdown(item)
    }
  }

  function syncBreakpoint() {
    closeAllDropdowns()

    items.forEach(item => {
      if (desktop.matches) {
        item.submenu.style.height = ''
      } else {
        item.submenu.style.height = '0px'
      }
    })

    previousScrollY = Math.max(window.scrollY, 0)
    isScrolled = desktop.matches ? false : previousScrollY > MOBILE_SCROLL_THRESHOLD
    navbar.classList.toggle('is-scrolled', isScrolled)
    navbar.classList.toggle('is-not-top', getIsNotTopState(previousScrollY))
  }

  items.forEach(item => {
    item.trigger.setAttribute('role', 'button')
    item.trigger.setAttribute('tabindex', '0')
    item.trigger.setAttribute('aria-expanded', 'false')

    item.trigger.addEventListener('click', event => {
      event.preventDefault()
      event.stopPropagation()
      toggleDropdown(item)
    })

    item.trigger.addEventListener('keydown', event => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return
      }

      event.preventDefault()
      event.stopPropagation()
      toggleDropdown(item)
    })
  })

  document.addEventListener('click', event => {
    if (
      !desktop.matches ||
      !activeItem ||
      navbar.contains(event.target)
    ) {
      return
    }

    closeAllDropdowns()
  })

  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape' || !activeItem) {
      return
    }

    const trigger = activeItem.trigger
    closeAllDropdowns()
    trigger.focus()
  })

  menuButton?.addEventListener('click', () => {
    window.requestAnimationFrame(() => {
      if (!menuButton.classList.contains('w--open')) {
        closeAllDropdowns()
      }
    })
  })

  window.addEventListener('scroll', updateScrollState, {passive: true})

  window.addEventListener('resize', () => {
    if (!activeItem) {
      return
    }

    if (desktop.matches) {
      overlay.style.height = `${getOverlayHeight(activeItem.submenu)}px`
    } else {
      activeItem.submenu.style.height = `${activeItem.submenu.scrollHeight}px`
    }
  })

  if (desktop.addEventListener) {
    desktop.addEventListener('change', syncBreakpoint)
  } else {
    desktop.addListener(syncBreakpoint)
  }

  syncBreakpoint()
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initUpdatedNavbar, {once: true})
} else {
  initUpdatedNavbar()
}
